import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { fmtINR, BUCKET_STYLE } from "@/lib/format";
import { usePullToRefresh } from "@/hooks/usePullToRefresh";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  CalendarClock, AlertTriangle, ArrowRight, Sparkles, Camera, Mic,
  TrendingUp, Trophy, AudioLines, IndianRupee,
} from "lucide-react";
import { toast } from "sonner";
import { StatTile, FollowupRow, greetingForNow } from "./_shared";
import PerfNotifBanner from "@/components/PerfNotifBanner";

/**
 * AdminDashboard — Original unified Home layout (pre-iteration-41).
 * 3 status tiles · quick capture · Sales Pulse leaderboard · 3-col follow-ups · reports snapshot.
 */
export default function AdminDashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [reports, setReports] = useState(null);
  const [board, setBoard] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const [home, rep, lb] = await Promise.all([
        api.get("/dashboard/home"),
        api.get("/reports/summary").catch(() => ({ data: null })),
        api.get("/admin/team/leaderboard").catch(() => ({ data: null })),
      ]);
      setData(home.data);
      setReports(rep.data);
      setBoard(lb.data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);
  const { indicator: pullIndicator } = usePullToRefresh(load);

  const completeFu = async (id) => {
    try {
      await api.post(`/followups/${id}/complete`);
      toast.success("Follow-up completed");
      load();
    } catch {
      toast.error("Could not complete");
    }
  };

  if (loading) {
    return (
      <div className="p-6 sm:p-8 space-y-6">
        <Skeleton className="h-12 w-2/3 rounded-2xl" />
        <div className="grid grid-cols-3 gap-4">
          <Skeleton className="h-28 rounded-3xl" />
          <Skeleton className="h-28 rounded-3xl" />
          <Skeleton className="h-28 rounded-3xl" />
        </div>
        <Skeleton className="h-64 rounded-3xl" />
      </div>
    );
  }

  const firstName = (user?.name || user?.email || "").split(/\s+/)[0];
  const boardRows = board?.rows || [];

  return (
    <div className="p-4 sm:p-8 lg:p-10 space-y-6 sm:space-y-8 max-w-[1180px] mx-auto" data-testid="dashboard-admin">
      {pullIndicator}
      <PerfNotifBanner />
      <header className="space-y-1.5 animate-fade-in-up">
        <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-muted-foreground">{greetingForNow()}</p>
        <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tight">
          {firstName}<span className="text-primary">.</span>
        </h1>
        <p className="text-sm text-muted-foreground max-w-md">
          Your sales memory for the day. Less form-filling, more closing.
        </p>
      </header>

      {/* Total sales this month — hero card */}
      <section data-testid="total-sales-card">
        <Card className="rounded-3xl border-primary/20 shadow-soft overflow-hidden bg-gradient-to-br from-primary-50 via-card to-card">
          <CardContent className="p-5 sm:p-7 flex items-center gap-4 sm:gap-6 flex-wrap">
            <div className="h-14 w-14 sm:h-16 sm:w-16 rounded-3xl bg-primary text-primary-foreground grid place-items-center shrink-0 shadow-soft">
              <IndianRupee className="h-7 w-7 sm:h-8 sm:w-8" />
            </div>
            <div className="flex-1 min-w-[200px]">
              <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-primary">Total sales · {new Date().toLocaleDateString("en-IN", { month: "long", year: "numeric" })}</p>
              <p className="font-display text-3xl sm:text-5xl font-black tracking-tight leading-none mt-2 text-primary-800" data-testid="total-sales-value">
                {fmtINR(boardRows.reduce((sum, r) => sum + (r.won_value || 0), 0))}
              </p>
              <p className="text-xs sm:text-sm text-muted-foreground mt-2">
                {boardRows.reduce((sum, r) => sum + (r.won_count || 0), 0)} deal{boardRows.reduce((sum, r) => sum + (r.won_count || 0), 0) !== 1 ? "s" : ""} closed · across {boardRows.filter((r) => r.won_count > 0).length} rep{boardRows.filter((r) => r.won_count > 0).length !== 1 ? "s" : ""}
              </p>
            </div>
            <Link to="/team" data-testid="total-sales-link" className="text-xs font-semibold text-primary hover:underline tap inline-flex items-center gap-1 self-start sm:self-center">
              View breakdown <ArrowRight className="h-3 w-3" />
            </Link>
          </CardContent>
        </Card>
      </section>

      <section className="grid grid-cols-3 gap-2 sm:gap-4">
        <StatTile label="Overdue" value={data.overdue_count} hint="Take action today" accent="text-red-600" icon={AlertTriangle} to="/followups?bucket=overdue" />
        <StatTile label="Due today" value={data.today_count} hint="Don't miss these" accent="text-amber-600" icon={CalendarClock} to="/followups?bucket=today" />
        <StatTile label="Upcoming" value={data.upcoming_count} hint="Plan your week" accent="text-primary-600" icon={TrendingUp} to="/followups?bucket=upcoming" />
      </section>

      {/* Quick capture */}
      <section className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <Link to="/ai-extract?tab=voice-journal" data-testid="quick-voice-journal" className="group p-4 rounded-3xl bg-primary text-primary-foreground tap card-lift shadow-soft">
          <AudioLines className="h-5 w-5" />
          <p className="font-semibold mt-2 text-sm">Record voice journal</p>
          <p className="text-[11px] opacity-80 mt-0.5">Talk · AI logs everything</p>
        </Link>
        <Link to="/ai-extract?tab=meeting" data-testid="quick-meeting" className="group p-4 rounded-3xl bg-card border border-border/60 tap card-lift shadow-soft">
          <Mic className="h-5 w-5 text-primary" />
          <p className="font-semibold mt-2 text-sm">Add meeting update</p>
          <p className="text-[11px] text-muted-foreground mt-0.5">Paste summary → AI extracts</p>
        </Link>
        <Link to="/ai-extract?tab=vcard" data-testid="quick-vcard" className="group p-4 rounded-3xl bg-card border border-border/60 tap card-lift shadow-soft">
          <Camera className="h-5 w-5 text-primary" />
          <p className="font-semibold mt-2 text-sm">Upload visiting card</p>
          <p className="text-[11px] text-muted-foreground mt-0.5">OCR contact details</p>
        </Link>
        <Link to="/deals?new=1" data-testid="quick-deal" className="group p-4 rounded-3xl bg-card border border-border/60 tap card-lift shadow-soft">
          <Sparkles className="h-5 w-5 text-primary" />
          <p className="font-semibold mt-2 text-sm">Add deal</p>
          <p className="text-[11px] text-muted-foreground mt-0.5">New opportunity</p>
        </Link>
        <Link to="/pipeline" data-testid="quick-pipeline" className="group p-4 rounded-3xl bg-card border border-border/60 tap card-lift shadow-soft">
          <ArrowRight className="h-5 w-5 text-primary" />
          <p className="font-semibold mt-2 text-sm">Open pipeline</p>
          <p className="text-[11px] text-muted-foreground mt-0.5">All stages</p>
        </Link>
      </section>

      {/* Sales Pulse leaderboard preview */}
      {boardRows.length > 0 && (
        <section data-testid="home-leaderboard">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Trophy className="h-4 w-4 text-primary" />
              <h2 className="font-display text-lg sm:text-xl font-extrabold">Sales Pulse · this month</h2>
            </div>
            <Link to="/team" data-testid="home-team-link" className="text-xs font-semibold text-primary hover:underline tap inline-flex items-center gap-1">
              View all <ArrowRight className="h-3 w-3" />
            </Link>
          </div>
          <div className="grid grid-cols-3 gap-2 sm:gap-4">
            {boardRows.slice(0, 3).map((r, i) => (
              <Link
                key={r.id}
                to="/team"
                data-testid={`home-podium-${i + 1}`}
                className="p-3 sm:p-5 rounded-3xl border-2 bg-gradient-to-br to-card tap card-lift overflow-hidden"
                style={{ borderColor: i === 0 ? "#fcd34d" : i === 1 ? "#d4d4d8" : "#fdba74" }}
              >
                <div className="flex items-center gap-1.5 mb-1.5">
                  <Trophy className={`h-3 w-3 ${i === 0 ? "text-yellow-600" : i === 1 ? "text-zinc-500" : "text-orange-600"}`} />
                  <span className="text-[9px] font-bold uppercase tracking-[0.14em] text-muted-foreground">#{i + 1}</span>
                </div>
                <p className="text-xs font-bold truncate">{r.name || r.email}</p>
                <p className="font-display text-base sm:text-2xl font-black tracking-tight mt-1 leading-none truncate">{fmtINR(r.won_value)}</p>
                <p className="text-[10px] text-muted-foreground mt-0.5">{r.won_count} deal{r.won_count !== 1 ? "s" : ""}</p>
              </Link>
            ))}
            {boardRows.length < 3 && Array.from({ length: 3 - boardRows.length }).map((_, i) => (
              <div key={`pe-${i}`} className="p-3 sm:p-5 rounded-3xl border-2 border-dashed border-border/60 text-center text-xs text-muted-foreground grid place-items-center min-h-[88px]">—</div>
            ))}
          </div>
        </section>
      )}

      {/* Follow-up lists */}
      <section className="grid lg:grid-cols-3 gap-5">
        {[
          { key: "overdue", title: "Overdue", rows: data.overdue, empty: "All caught up. Nothing overdue." },
          { key: "today", title: "Due today", rows: data.today, empty: "Nothing due today." },
          { key: "upcoming", title: "Upcoming", rows: data.upcoming, empty: "No upcoming follow-ups." },
        ].map((sec) => {
          const s = BUCKET_STYLE[sec.key];
          return (
            <Card key={sec.key} className="rounded-3xl border-border/60 shadow-soft overflow-hidden" data-testid={`section-${sec.key}`}>
              <CardContent className="p-4 sm:p-5">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <span className={`h-2 w-2 rounded-full ${s.dot}`} />
                    <h3 className="font-display text-lg font-extrabold">{sec.title}</h3>
                  </div>
                  <Badge variant="outline" className={`${s.chip} text-[10px] font-bold uppercase tracking-wider`}>
                    {sec.rows.length}
                  </Badge>
                </div>
                <div className="space-y-2">
                  {sec.rows.length === 0 ? (
                    <p className="text-sm text-muted-foreground py-6 text-center">{sec.empty}</p>
                  ) : (
                    sec.rows.slice(0, 6).map((fu) => <FollowupRow key={fu.id} fu={fu} onComplete={completeFu} onPing={load} />)
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </section>

      {/* Reports snapshot */}
      {reports && (
        <section className="grid sm:grid-cols-4 gap-4">
          <Card className="rounded-3xl border-border/60 shadow-soft">
            <CardContent className="p-5">
              <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground">Leads value</p>
              <p className="font-display text-2xl font-black mt-2">{fmtINR(reports.leads_value)}</p>
            </CardContent>
          </Card>
          <Card className="rounded-3xl border-border/60 shadow-soft">
            <CardContent className="p-5">
              <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground">RFQ value</p>
              <p className="font-display text-2xl font-black mt-2">{fmtINR(reports.rfq_value)}</p>
            </CardContent>
          </Card>
          <Card className="rounded-3xl border-border/60 shadow-soft">
            <CardContent className="p-5">
              <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground">Won</p>
              <p className="font-display text-2xl font-black mt-2 text-primary">{fmtINR(reports.won_value)}</p>
              <p className="text-xs text-muted-foreground">{reports.won_count} deals</p>
            </CardContent>
          </Card>
          <Card className="rounded-3xl border-border/60 shadow-soft">
            <CardContent className="p-5">
              <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground">Conversion</p>
              <p className="font-display text-2xl font-black mt-2">{reports.conversion_pct}%</p>
              <p className="text-xs text-muted-foreground">won / total deals</p>
            </CardContent>
          </Card>
        </section>
      )}
    </div>
  );
}
